import { startOfLocalDayInUtc, endOfLocalDayInUtc } from "./dateUtils";
import {
  endOfIanaCalendarDayUtc,
  resolveTradeListWindowQuery,
  startOfIanaCalendarDayUtc,
} from "./notificationPeriod";
import type { Trade } from "../types/trade";

export function normalizeInstrumentSymbol(s: string): string {
  return String(s || "").replace(/[/\s:-]/g, "").toUpperCase();
}

export function tradeMatchesSymbol(tradeSymbol: string, chartSymbol: string): boolean {
  return normalizeInstrumentSymbol(tradeSymbol) === normalizeInstrumentSymbol(chartSymbol);
}

export function parseTradeTimeMs(value: Date | string | null | undefined): number | null {
  if (value == null) return null;
  const d = typeof value === "string" ? new Date(value) : value;
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function isTradeDust(trade: Trade): boolean {
  return Boolean(trade.positionSemantics?.is_dust);
}

export function isTradeScratch(trade: Trade): boolean {
  return Boolean(trade.positionSemantics?.is_scratch);
}

/** Hidden from Trade Review unless “Include dust & scratch positions” is on (BE `is_dust` only). */
export function isReviewNoiseTrade(trade: Trade, includeDust: boolean): boolean {
  if (includeDust) return false;
  return isTradeDust(trade);
}

/**
 * Whether a trade belongs in the review window.
 * - Closed: lifespan intersects [startMs, endMs].
 * - Open: opened inside the window only (stale csv-import opens from 2021 must not match “last week”).
 */
export function tradeOverlapsWindow(trade: Trade, startMs: number, endMs: number): boolean {
  const entryMs = parseTradeTimeMs(trade.entryDate);
  if (entryMs == null) return false;

  const status = String(trade.status ?? "").toLowerCase();
  if (status === "open") {
    return entryMs >= startMs && entryMs <= endMs;
  }

  const exitMs = parseTradeTimeMs(trade.exitDate);
  const lifeEndMs = exitMs ?? entryMs;
  return entryMs <= endMs && lifeEndMs >= startMs;
}

/**
 * Report / Statistics cohort membership: closed trade whose **exit** falls in [startMs, endMs].
 * Open trades are never included.
 */
export function tradeClosedExitInWindow(trade: Trade, startMs: number, endMs: number): boolean {
  if (String(trade.status ?? "").trim().toLowerCase() === "open") return false;
  const exitMs = parseTradeTimeMs(trade.exitDate);
  if (exitMs == null) return false;
  return exitMs >= startMs && exitMs <= endMs;
}

/** Overlay row cohort check (exit_time in window). */
export function overlayClosedExitInWindow(
  overlay: { exit_time?: string | null; status?: string | null },
  startMs: number,
  endMs: number
): boolean {
  if (String(overlay.status ?? "").trim().toLowerCase() === "open") return false;
  const exitMs = parseTradeTimeMs(overlay.exit_time ?? null);
  if (exitMs == null) return false;
  return exitMs >= startMs && exitMs <= endMs;
}

export type ReviewWindowMembership = "overlap" | "closed_exit";

function tradeMatchesMembership(
  trade: Trade,
  startMs: number,
  endMs: number,
  membership: ReviewWindowMembership
): boolean {
  return membership === "closed_exit"
    ? tradeClosedExitInWindow(trade, startMs, endMs)
    : tradeOverlapsWindow(trade, startMs, endMs);
}

export type ReviewWindowMode = "date" | "range" | "timeframe";

export type ReviewWindowBounds = {
  mode: ReviewWindowMode;
  startMs: number;
  endMs: number;
  /** Single-day YYYY-MM-DD when mode === "date". */
  dateYmd?: string;
  timeframe?: string;
};

function formatTodayInZone(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Calendar bounds for trade filtering (no candle start buffer). */
export function resolveReviewWindowBounds(
  mode: ReviewWindowMode,
  opts: {
    dateYmd?: string;
    start_date?: string;
    end_date?: string;
    timeframe?: string;
    reportingTimeZone: string;
  }
): ReviewWindowBounds | null {
  const zone = opts.reportingTimeZone?.trim() || "UTC";

  if (mode === "date") {
    const ymd = String(opts.dateYmd || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
    const start = startOfLocalDayInUtc(new Date(y, m - 1, d));
    const end = endOfLocalDayInUtc(new Date(y, m - 1, d));
    return { mode, startMs: start.getTime(), endMs: end.getTime(), dateYmd: ymd };
  }

  if (mode === "range") {
    const start = opts.start_date ? new Date(opts.start_date) : null;
    const end = opts.end_date ? new Date(opts.end_date) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    if (end.getTime() < start.getTime()) return null;
    return { mode, startMs: start.getTime(), endMs: end.getTime() };
  }

  const tf = opts.timeframe;
  if (!tf) return null;

  const window = resolveTradeListWindowQuery(
    { startDate: null, endDate: null },
    tf,
    zone,
    { includeTimezoneQuery: false }
  );

  if (window.kind === "local") {
    const start = startOfIanaCalendarDayUtc(window.period.local_start_date, zone);
    const end = endOfIanaCalendarDayUtc(window.period.local_end_date, zone);
    return {
      mode,
      startMs: start.getTime(),
      endMs: end.getTime(),
      timeframe: tf,
    };
  }

  if (window.timeframe === "all-time") {
    const endYmd = formatTodayInZone(zone);
    const end = endOfIanaCalendarDayUtc(endYmd, zone);
    return { mode, startMs: 0, endMs: end.getTime(), timeframe: tf };
  }

  const endYmd = formatTodayInZone(zone);
  const start = startOfIanaCalendarDayUtc(endYmd, zone);
  const end = endOfIanaCalendarDayUtc(endYmd, zone);
  return { mode, startMs: start.getTime(), endMs: end.getTime(), timeframe: tf };
}

export function filterTradesInReviewWindow(
  trades: Trade[],
  bounds: ReviewWindowBounds,
  options?: {
    includeDust?: boolean;
    symbol?: string;
    /** Default `overlap`. Reports use `closed_exit` (exit date in window). */
    membership?: ReviewWindowMembership;
  }
): Trade[] {
  const includeDust = options?.includeDust ?? false;
  const membership = options?.membership ?? "overlap";
  return trades.filter((t) => {
    if (!t?.symbol) return false;
    if (isReviewNoiseTrade(t, includeDust)) return false;
    if (options?.symbol && !tradeMatchesSymbol(t.symbol, options.symbol)) return false;
    return tradeMatchesMembership(t, bounds.startMs, bounds.endMs, membership);
  });
}

export function rankSymbolsInReviewWindow(
  trades: Trade[],
  bounds: ReviewWindowBounds,
  includeDust: boolean,
  membership: ReviewWindowMembership = "overlap"
): Array<{ symbol: string; count: number }> {
  const counts = new Map<string, number>();
  for (const t of trades) {
    if (!t?.symbol) continue;
    if (isReviewNoiseTrade(t, includeDust)) continue;
    if (!tradeMatchesMembership(t, bounds.startMs, bounds.endMs, membership)) continue;
    counts.set(t.symbol, (counts.get(t.symbol) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([symbol, count]) => ({ symbol, count }))
    .sort((a, b) => b.count - a.count || a.symbol.localeCompare(b.symbol));
}

export function formatReviewWindowLabel(bounds: ReviewWindowBounds): string {
  return formatChunkRangeLabel(bounds.startMs, bounds.endMs);
}

function formatChunkRangeLabel(startMs: number, endMs: number): string {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${fmt.format(new Date(startMs))} → ${fmt.format(new Date(endMs))}`;
}
